// Super-admin "log in as user" impersonation state.
//
// Stored in localStorage (not sessionStorage) so it is shared across tabs of the
// same origin — e.g. opening an order in a new tab keeps the admin acting as the
// selected client. Because localStorage persists until explicitly removed, callers
// MUST clear it on sign-in and sign-out (see App.js auth observer + Auth/SSO sign-in)
// so a stale client_id can never leak into a different user's session.
const IMPERSONATION_KEY = 'client_id';

// Organization an Onward admin is simulating as its org admin. App.js overlays it onto the
// admin's own user (roles → ADMIN, organization → this org); a client_id can sit on top of it
// the same way a real org admin picks one of their accounts.
const ORG_IMPERSONATION_KEY = 'impersonated_organization_id';

const read = (key) => {
    try {
        return window?.localStorage?.getItem(key) || null;
    } catch {
        return null;
    }
};

const write = (key, value) => {
    try {
        window?.localStorage?.setItem(key, value);
    } catch {
        /* storage unavailable (e.g. private mode) — no-op */
    }
};

const remove = (key) => {
    try {
        window?.localStorage?.removeItem(key);
    } catch {
        /* storage unavailable (e.g. private mode) — no-op */
    }
};

/**
 * The client an admin is acting as, or null.
 * @returns {string|null}
 */
export const getImpersonatedClientId = () => read(IMPERSONATION_KEY);

/**
 * Start acting as a client. Survives a refresh and is shared across tabs.
 * @param {string} clientId
 */
export const setImpersonatedClientId = (clientId) => write(IMPERSONATION_KEY, clientId);

/** Stops acting as a client. Leaves a simulated organization in place, like picking "All Accounts". */
export const clearImpersonatedClientId = () => remove(IMPERSONATION_KEY);

/**
 * The organization an Onward admin is simulating, or null.
 * @returns {string|null}
 */
export const getImpersonatedOrgId = () => read(ORG_IMPERSONATION_KEY);

/**
 * Start simulating an organization's org admin view.
 * @param {string} organizationId
 */
export const setImpersonatedOrgId = (organizationId) => write(ORG_IMPERSONATION_KEY, organizationId);

/** Clears every kind of impersonation (client and organization). Use on sign-in, sign-out and exiting a sim. */
export const clearImpersonation = () => {
    remove(IMPERSONATION_KEY);
    remove(ORG_IMPERSONATION_KEY);
};
